import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ChevronRight,
  Trophy,
  Lock,
  TrendingUp,
  Share2,
  Users,
} from "lucide-react";
import type { StartHomeCase } from "@workspace/profile-completion";
import { getCountryName } from "@workspace/profile-completion";

export interface StartHomeLeagueRow {
  id: string;
  name: string;
  memberCount: number;
  isNational?: boolean;
}

interface StartHomeCardProps {
  startHomeCase: StartHomeCase;
  playerCountry?: string | null;
  nationalPlayerCount?: number | null;
  skillLevel?: number | null;
  featuredLeagueName?: string | null;
  leagueRows: StartHomeLeagueRow[];
  onCreateLeague: () => void;
  onJoinByCode: () => void;
  onExploreNational: () => void;
  onInviteLeague: () => void;
  onRegisterMatch: () => void;
  onViewLeague: (leagueId: string) => void;
}

function formatPlayerCount(n: number): string {
  return `${n.toLocaleString("es-DO")} jugador${n === 1 ? "" : "es"}`;
}

function countryFlagEmoji(code: string): string {
  return [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

export function StartHomeCard({
  startHomeCase,
  playerCountry,
  nationalPlayerCount,
  skillLevel,
  featuredLeagueName,
  leagueRows,
  onCreateLeague,
  onJoinByCode,
  onExploreNational,
  onInviteLeague,
  onRegisterMatch,
  onViewLeague,
}: StartHomeCardProps) {
  const countryLabel = playerCountry ? getCountryName(playerCountry) : null;
  const levelLabel =
    typeof skillLevel === "number" ? skillLevel.toFixed(2) : "1.00";

  return (
    <div className="space-y-6" data-testid="start-home-card">
      <div className="mb-2">
        {startHomeCase === "A" && (
          <>
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              Empieza aquí
            </h1>
            <p className="text-lg text-muted-foreground mb-4">
              2 pasos para tu primer partido
            </p>
            {countryLabel && (
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                <Trophy className="h-4 w-4" />
                Ya estás en la Liga Nacional · {countryLabel}
              </div>
            )}
          </>
        )}
        {startHomeCase === "B" && (
          <>
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              Ya casi
            </h1>
            <p className="text-lg text-muted-foreground">
              Solo falta jugar tu primer partido
            </p>
          </>
        )}
        {startHomeCase === "C" && (
          <>
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              Tu liga está casi lista
            </h1>
            <p className="text-lg text-muted-foreground">
              Invita jugadores para poder jugar
            </p>
          </>
        )}
      </div>

      {startHomeCase === "A" && (
        <>
          <Card
            className="border-2 border-primary/30 p-6 relative overflow-hidden"
            data-testid="card-start-home-step-1"
          >
            <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none">
              <Trophy className="h-24 w-24" />
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold">
                1
              </div>
              <div className="space-y-4 w-full">
                <div>
                  <h2 className="text-xl font-semibold">
                    Encuentra con quién jugar
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    Crea tu propia liga con amigos o únete a una existente
                    para empezar a registrar partidos.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={onCreateLeague}
                    data-testid="button-start-home-create-league"
                  >
                    Crear mi liga
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onJoinByCode}
                    data-testid="button-start-home-join-code"
                  >
                    Unirme con código
                  </Button>
                </div>
                <div className="pt-2">
                  <Button
                    variant="ghost"
                    className="px-0 h-auto min-h-0 text-primary hover:text-primary/80 hover:bg-transparent group"
                    onClick={onExploreNational}
                    data-testid="button-start-home-explore-national"
                  >
                    Explorar Liga Nacional
                    <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <Card
            className="border p-6 opacity-60 bg-muted/30"
            data-testid="card-start-home-step-2"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted-foreground/30 text-muted-foreground font-bold">
                2
              </div>
              <div className="space-y-4 w-full">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-muted-foreground">
                      Registra tu primer partido
                    </h2>
                    <p className="text-muted-foreground mt-1">
                      Cuando juegues, regístralo aquí
                    </p>
                  </div>
                  <Lock className="h-5 w-5 text-muted-foreground/50" />
                </div>
                <div className="pt-2">
                  <Button
                    disabled
                    variant="secondary"
                    className="pointer-events-none"
                  >
                    Registrar resultado
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      {startHomeCase === "B" && (
        <Card
          className="border-2 border-primary/30 p-8 relative overflow-hidden bg-primary/5"
          data-testid="card-start-home-main-step"
        >
          <div className="absolute top-0 right-0 p-6 opacity-10 pointer-events-none">
            <Trophy className="h-32 w-32 text-primary" />
          </div>
          <div className="space-y-6 relative z-10">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">
                Registra tu primer partido
                {featuredLeagueName ? ` en ${featuredLeagueName}` : ""}
              </h2>
              <p className="text-muted-foreground mt-2 max-w-md">
                Empieza a sumar puntos y competir con otros jugadores en tu
                liga.
              </p>
            </div>
            <div>
              <Button
                size="lg"
                className="text-base h-12 px-8"
                onClick={onRegisterMatch}
                data-testid="button-start-home-register-match"
              >
                Registrar partido
              </Button>
            </div>
            <div className="flex items-center gap-4 pt-4">
              <Button
                variant="ghost"
                className="px-0 h-auto min-h-0 text-muted-foreground hover:text-foreground hover:bg-transparent"
                onClick={onCreateLeague}
                data-testid="button-start-home-create-league-secondary"
              >
                Crear otra liga
              </Button>
              <Button
                variant="ghost"
                className="px-0 h-auto min-h-0 text-muted-foreground hover:text-foreground hover:bg-transparent"
                onClick={onJoinByCode}
                data-testid="button-start-home-join-code-secondary"
              >
                Unirme con código
              </Button>
            </div>
          </div>
        </Card>
      )}

      {startHomeCase === "C" && (
        <>
          <Card
            className="border-2 border-primary/30 p-6 relative overflow-hidden"
            data-testid="card-start-home-step-1"
          >
            <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none">
              <Users className="h-24 w-24" />
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold">
                1
              </div>
              <div className="space-y-4 w-full">
                <div>
                  <h2 className="text-xl font-semibold">
                    Invita a {featuredLeagueName ?? "tu liga"}
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    Comparte el enlace o código para que tus amigos se unan.
                  </p>
                </div>
                <div>
                  <Button
                    onClick={onInviteLeague}
                    data-testid="button-start-home-invite-league"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Invitar
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <Card
            className="border p-6 opacity-60 bg-muted/30"
            data-testid="card-start-home-step-2"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted-foreground/30 text-muted-foreground font-bold">
                2
              </div>
              <div className="space-y-4 w-full">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-muted-foreground">
                      Registra tu primer partido
                    </h2>
                    <p className="text-muted-foreground mt-1">
                      Se activa cuando alguien se una a tu liga
                    </p>
                  </div>
                  <Lock className="h-5 w-5 text-muted-foreground/50" />
                </div>
                <div className="pt-2">
                  <Button
                    disabled
                    variant="secondary"
                    className="pointer-events-none"
                  >
                    Registrar resultado
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      <div className="flex items-center gap-2 mt-2">
        <Badge
          variant="secondary"
          className="px-3 py-1.5 text-xs font-medium rounded-full bg-secondary/80 flex items-center gap-1.5 text-muted-foreground"
          data-testid="badge-start-home-initial-level"
        >
          <TrendingUp className="h-3.5 w-3.5" />
          Tu nivel inicial {levelLabel} · se ajusta con tus partidos
        </Badge>
      </div>

      <div className="mt-2 space-y-2" data-testid="list-start-home-leagues">
        <h3 className="text-sm font-semibold text-muted-foreground px-1">
          Tus Ligas
        </h3>
        {leagueRows.map((league) => (
          <button
            key={league.id}
            onClick={() => onViewLeague(league.id)}
            className="w-full group cursor-pointer rounded-xl border bg-card p-4 transition-all hover:shadow-md hover:border-primary/20 flex items-center gap-4 text-left"
            data-testid={`row-start-home-league-${league.id}`}
          >
            {league.isNational && playerCountry ? (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted text-2xl">
                {countryFlagEmoji(playerCountry)}
              </div>
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Trophy className="h-6 w-6" />
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              <h3 className="font-semibold text-sm truncate">
                {league.name}
              </h3>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {league.isNational && typeof nationalPlayerCount === "number"
                  ? formatPlayerCount(nationalPlayerCount)
                  : formatPlayerCount(league.memberCount)}
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
